import { Link } from '@tanstack/react-router'
import { ArrowLeft, ShieldAlert, ShieldCheck } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export function AccessDeniedPage() {
  return (
    <div className="flex w-full justify-center">
      <Card className="relative w-full max-w-3xl overflow-hidden border-border/70 bg-card shadow-xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
        <CardHeader className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex size-14 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
              <ShieldAlert className="size-7" aria-hidden />
            </div>
            <div className="space-y-1">
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                Restricted area
              </Badge>
              <CardTitle className="text-3xl">Access denied</CardTitle>
            </div>
          </div>
          <CardDescription className="text-base leading-relaxed">
            You don’t have permission to view this space. If you believe this is an error, reach out to an administrator so they can adjust your role or grant temporary access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border/70 bg-muted/40 p-4 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ShieldCheck className="size-4 text-primary" aria-hidden />
                Quick options
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Continue with the areas you can access or return to your dashboard.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button asChild size="sm">
                  <Link to="/leave/applications">Go to my leave</Link>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <Link to="/">Back to dashboard</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-lg border border-border/70 bg-muted/40 p-4 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ShieldCheck className="size-4 text-primary" aria-hidden />
                Need access?
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Contact your system administrator with the page link and a short note on why you need access.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button asChild size="sm" variant="ghost" className="gap-2 text-muted-foreground">
                  <Link to="/leave/approvals">
                    <ArrowLeft className="size-4" aria-hidden />
                    Back to previous area
                  </Link>
                </Button>
                <a
                  className="text-sm font-medium text-primary hover:underline"
                  href="mailto:admin@uibfs.or.ug?subject=Access%20request"
                >
                  Email administrator
                </a>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Tip: roles are enforced per section. If your access was recently updated, try refreshing after the administrator confirms your role.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
